import type { StudentRow } from '../../../shared/api/classes'
import type { EventRow, StrengthsReport } from '../../../shared/api/teaching'
import type { ProgressSnapshot, SessionRow } from '../../../shared/api/student'
import type { Lang } from './copy'
import { ADMIN_COPY } from './adminCopy'
import {
  identityRows,
  sheetSections,
  type SheetRow,
  type SheetSection,
} from './studentProfileSheetModel'

/**
 * The document a head teacher prints from E8.
 *
 * 🔴 "Export Profile" WAS `alert('Export Profile PDF triggered')`. This is the
 * other half of it, and it is a SHEET rather than the screen with its buttons
 * hidden — for the reason `print.css` and `ReportSheet` already record at
 * length. `DashboardShell` is `h-dvh overflow-hidden` with the page inside an
 * `overflow-auto` column, so a browser handed the screen prints ONE VIEWPORT:
 * a sidebar down the left, a tab strip, and the child's progress clipped off the
 * bottom. Nobody files that.
 *
 * 🔴 IT IS IN THE DOM AND HIDDEN, NOT BUILT ON DEMAND. `window.print()` is
 * synchronous — there is no moment between the click and the dialogue in which
 * to fetch anything — so everything the document needs has to be loaded already.
 * It takes each resource's `data` rather than waiting on it, and prints what
 * arrived; `studentProfileSheetModel` turns a missing read into a stated
 * sentence rather than into a zero.
 *
 * 🔴 AND IT CARRIES NOTHING FROM THE OVERVIEW TAB. That tab still draws four
 * invented subject percentages, an attendance ring the product never computed
 * and a hardcoded joining date. On screen they are a bug; on a page that names a
 * child, printed and handed to that child's parents, they would be the product
 * asserting something untrue about a four-year-old. Every figure below came off
 * a request.
 *
 * ⚠️ ONE COLUMN, NO CARDS, NO SHADOWS. A document is not a dashboard: the
 * screen's three-column grid is a layout for a 1440px window and prints as three
 * narrow gutters. `print-keep` on each section is `break-inside: avoid`, so a
 * heading never lands at the foot of a page with its numbers overleaf.
 */

export interface StudentProfileSheetProps {
  student: StudentRow
  lang: Lang
  /** Null while a read is in flight or after it failed — see the model. */
  progress: ProgressSnapshot | null
  strengths: StrengthsReport | null
  sessions: SessionRow[] | null
  events: EventRow[] | null
  /** When the sheet was drawn. Passed in, never read from a clock in render. */
  now: Date
}

export function StudentProfileSheet({
  student,
  lang,
  progress,
  strengths,
  sessions,
  events,
  now,
}: StudentProfileSheetProps) {
  const t = ADMIN_COPY[lang]
  const sections = sheetSections({ student, progress, strengths, sessions, events, lang })

  return (
    /*
     * ⚠️ `hidden print:block` — INVISIBLE ON SCREEN, THE PAGE ON PAPER. The
     * screen and the sheet are two documents about the same child; printing both
     * would put a copy of the app underneath the report. `print.css` supplies
     * `.print-sheet`'s full-width layout and strips the shell around it.
     */
    <article className="print-sheet hidden print:block font-dash text-ink">
      <header className="print-keep border-b-2 border-ink pb-3">
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-dash-2xs font-black tracking-wider uppercase">Kalqy</span>
          <span className="text-dash-2xs text-muted">{t.sheetPrepared(now.toLocaleString())}</span>
        </div>
        <h1 className="mt-2 text-dash-xl font-extrabold">{student.name}</h1>
        <p className="text-dash-xs font-bold text-muted">
          {t.sheetTitle} · {student.className}
        </p>
      </header>

      {/*
        🔴 THE SENTENCE THAT MAKES THE PAGE READABLE BY SOMEBODY WHO WAS NOT
        HERE. A printed page outlives its context — it goes into a file, into a
        meeting, home in a bag — so it has to say what its numbers are and are
        not. §12's rule about status applies to a document too: never a figure
        alone.
      */}
      <p className="print-keep mt-4 max-w-[62ch] text-dash-2xs text-muted">{t.sheetSource}</p>

      <section className="print-keep mt-5">
        <h2 className="border-b border-line pb-1 text-dash-sm font-extrabold uppercase tracking-wide">
          {t.sheetIdentity}
        </h2>
        <dl className="mt-2 grid grid-cols-2 gap-x-8 gap-y-1.5">
          {identityRows(student, lang).map((row) => (
            <SheetFact key={row.label} row={row} />
          ))}
        </dl>
        {/* ⚠️ ADR-004's ceiling, stated on the page rather than left as a gap. */}
        <p className="mt-2 max-w-[62ch] text-dash-2xs text-muted">{t.noOtherData}</p>
      </section>

      {sections.map((section) => (
        <SheetBlock key={section.title} section={section} lang={lang} />
      ))}
    </article>
  )
}

/** One `label: value` pair, with its evidence under it when it has any. */
function SheetFact({ row }: { row: SheetRow }) {
  return (
    <div className="flex flex-col py-0.5">
      <dt className="text-dash-2xs font-bold uppercase tracking-wide text-muted">{row.label}</dt>
      <dd className="text-dash-xs font-extrabold">
        {row.value}
        {row.detail !== undefined && (
          <span className="ml-2 text-dash-2xs font-semibold text-muted">{row.detail}</span>
        )}
      </dd>
    </div>
  )
}

/**
 * One section, in whichever of its three states it is in.
 *
 * ⚠️ AN EMPTY SECTION AND A SECTION THAT NEVER LOADED PRINT DIFFERENT SENTENCES,
 * and that difference is the whole reason the model returns a state rather than
 * an array. "Nothing recorded yet" is a fact about the child; "had not loaded"
 * is a fact about the print.
 */
function SheetBlock({ section, lang }: { section: SheetSection; lang: Lang }) {
  const t = ADMIN_COPY[lang]

  return (
    <section className="print-keep mt-5">
      <h2 className="border-b border-line pb-1 text-dash-sm font-extrabold uppercase tracking-wide">
        {section.title}
      </h2>
      {section.note !== undefined && (
        <p className="mt-1.5 max-w-[62ch] text-dash-2xs text-muted">{section.note}</p>
      )}

      {section.state === 'rows' ? (
        <dl className="mt-2 grid grid-cols-2 gap-x-8 gap-y-1.5">
          {section.rows.map((row, i) => (
            <SheetFact key={`${row.label}-${String(i)}`} row={row} />
          ))}
        </dl>
      ) : (
        <p className="mt-2 text-dash-xs font-semibold text-muted">
          {section.state === 'empty' ? t.sheetNothingYet : t.sheetNotLoaded}
        </p>
      )}
    </section>
  )
}

export default StudentProfileSheet
